import { getSessionId } from "@outschool/attribution";
import { CookieKeys } from "@outschool/data-schemas";
import { IPContext } from "@outschool/iplookup-client";
import Cookies from "js-cookie";

import pkg from "../lib/pkg";
import {
  formatLocationInfo,
  getLocalStorageItemWithTTL,
  injectScript,
  logDebugEvent,
  setLocalStorageItemWithTTL,
} from "../lib/util";
import {
  Integration,
  IntegrationCategory,
} from "../providers/AnalyticsContext";
import { PageContext } from "../providers/PageContext";
import { AnalyticsPlugin, AnalyticsPluginType } from ".";

import type { Context } from "../types";

declare global {
  interface Window {
    sessionReplay?: {
      init: (_: string, obj: any) => void;
      getSessionReplayProperties: () => any;
      setSessionId: (_: number) => void;
      shutdown: () => void;
    };
  }
}
/*
 * This plugin is for session Replay function of Amplitude
 * is aim to use when Amplitude is tracked
 */

const AMPLITUDE_SESSION_REPLAY_WHITELIST_ROUTES: string[] = [];
const BANNED_SUBDIVISIONS = ["ca", "pa", "il"];
const SESSION_FLAG = "AMPLITUDE_SESSION_REPLAY_ENABLED";
const SESSION_EXPIRY = 20 * 60 * 1000; // 20 mins
const SCRIPT_ID = "amplitudeSessionReplay2";
const SCRIPT_SRC =
  "https://cdn.amplitude.com/libs/session-replay-browser-1.30.0-min.js.gz";

enum SessionReplayGroup {
  ArizonaESA = "arizona-esa",
  Other = "other",
}

const isArizonaESAPage = (pagePath: string) => {
  const arizonaESAPaths = ["/lp/arizona", "/online-classes/arizona-esa"];
  return arizonaESAPaths.includes(pagePath);
};

const getSessionReplayGroup = (
  windowLocation: Location,
): SessionReplayGroup => {
  const pagePath = windowLocation.pathname ?? "";
  if (isArizonaESAPage(pagePath)) {
    return SessionReplayGroup.ArizonaESA;
  }
  return SessionReplayGroup.Other;
};

export default class AmplitudeSessionReplayPlugin implements AnalyticsPlugin {
  name = Integration.AmplitudeSessionReplay;
  category = IntegrationCategory.Functional;
  type = AnalyticsPluginType.enrichment;
  version = "0.1.0";
  sampleRate = 1; // wll override this in the amplitude platform.
  locationInfo: IPContext;
  pageContext: PageContext;
  isSessionReplayInitialized = false;

  shouldStartTracking = false;

  constructor({
    locationInfo,
    page,
  }: {
    locationInfo: IPContext;
    page: PageContext;
  }) {
    this.locationInfo = locationInfo;
    this.pageContext = page;
  }
  isLoadable() {
    const formatIpInfo = formatLocationInfo(this.locationInfo?.ipInfo);
    const shouldDisable =
      this.locationInfo.isInCA ||
      (!!formatIpInfo.ip_subdivision &&
        BANNED_SUBDIVISIONS.includes(
          formatIpInfo.ip_subdivision.toLowerCase(),
        ));

    return pkg.isProduction && !shouldDisable;
  }

  private async initializeSessionReplay(): Promise<void> {
    if (!pkg.amplitudeKey || typeof window === "undefined") {
      return;
    }

    await injectScript(SCRIPT_ID, SCRIPT_SRC);

    if (!window?.sessionReplay) {
      return;
    }

    const isSessionActive = getLocalStorageItemWithTTL(SESSION_FLAG) === "true";
    const pagePath = window?.location?.pathname ?? "";

    // loadable will remove the whole plugin, so it won't call page function when it change page,
    // so we need to conditionally init session replay here
    const shouldStartTracking =
      AMPLITUDE_SESSION_REPLAY_WHITELIST_ROUTES.includes(pagePath) ||
      isArizonaESAPage(pagePath);

    this.shouldStartTracking = !!shouldStartTracking;

    const shouldInitialize = isSessionActive || shouldStartTracking;

    if (shouldInitialize) {
      try {
        window.sessionReplay.init(pkg.amplitudeKey, {
          deviceId: Cookies.get(CookieKeys.AnonymousId),
          sessionId: getSessionId(),
          sampleRate: 0.1,
          debugMode: !pkg.isProduction,
        });

        this.isSessionReplayInitialized = true;
        if (!isSessionActive) {
          setLocalStorageItemWithTTL(SESSION_FLAG, "true", SESSION_EXPIRY);
        }
      } catch (error) {
        console.log("init error", error);
      }
    }
  }

  async load(): Promise<void> {
    await this.initializeSessionReplay();
  }

  async unload(): Promise<void> {
    if (!this.isLoaded()) {
      return;
    }
    window.sessionReplay?.shutdown();
    const script = document.getElementById(SCRIPT_ID);
    script?.remove();
    window.sessionReplay = undefined;
  }

  isLoaded(): boolean {
    return typeof window !== "undefined" && !!window?.sessionReplay;
  }

  private handlePageNavigation(): void {
    const isSessionActive = getLocalStorageItemWithTTL(SESSION_FLAG) === "true";

    if (
      !this.shouldStartTracking &&
      !isSessionActive &&
      this.isSessionReplayInitialized
    ) {
      window.sessionReplay?.shutdown();

      this.isSessionReplayInitialized = false;
      return;
    }

    if (
      (this.shouldStartTracking || isSessionActive) &&
      !this.isSessionReplayInitialized
    ) {
      this.initializeSessionReplay();
    }
  }

  page(context: Context): Context {
    this.handlePageNavigation();

    return context;
  }

  track(context: Context): Context {
    const isSessionActive = getLocalStorageItemWithTTL(SESSION_FLAG) === "true";

    if (!this.shouldStartTracking && !isSessionActive) {
      return context;
    }

    if (!!window.sessionReplay && this.isSessionReplayInitialized) {
      const sessionReplayProperties = {
        ...window.sessionReplay.getSessionReplayProperties(),
        session_replay_group: getSessionReplayGroup(window.location),
      };

      context.event = {
        ...context.event,
        properties: {
          ...context.event.properties,
          ...sessionReplayProperties,
        },
      };

      logDebugEvent(context.event);
    }

    return context;
  }
}
